import React, { useEffect, useRef } from 'react';
import './OtpInput.css';

/**
 * Saisie d'un code à N chiffres, une case par chiffre.
 *
 * Tout ce qui ressemble à un code collé ou autorempli est réparti sur les cases, d'où
 * qu'il vienne : collage (« 123 456 », « Code : 123456 »), suggestion iOS/macOS tirée
 * du mail (`autocomplete="one-time-code"` sur la 1re case), ou gestionnaire de mots de
 * passe qui remplit une seule case avec le code entier. Pour cette raison les cases
 * n'ont PAS de `maxLength=1` : il tronquerait l'autoremplissage au premier chiffre.
 *
 * `value` est une chaîne de 0 à N chiffres ; `onComplete` part quand la dernière case
 * est remplie, ce qui évite de devoir viser le bouton « Valider ».
 */
const OtpInput = ({
  value = '', onChange, onComplete, length = 6, label = 'Code', autoFocus = true,
  disabled = false, error = false, idPrefix = 'otp',
}) => {
  const refs = useRef([]);
  const digits = Array.from({ length }, (_, i) => value[i] || '');

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const focusAt = (i) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, i))];
    if (el) { el.focus(); el.select(); }
  };

  const commit = (next) => {
    const clean = next.replace(/\D/g, '').slice(0, length);
    onChange(clean);
    if (clean.length === length && onComplete) onComplete(clean);
    return clean;
  };

  // Écrit `typed` à partir de la case `i` : un chiffre remplace la case, plusieurs
  // (collage, autoremplissage) débordent sur les suivantes.
  const writeFrom = (i, typed) => {
    const incoming = typed.replace(/\D/g, '');
    if (!incoming) return;
    // Code complet reçu dans n'importe quelle case : il remplace tout.
    if (incoming.length >= length) {
      commit(incoming);
      focusAt(length - 1);
      return;
    }
    const arr = digits.slice();
    for (let k = 0; k < incoming.length && i + k < length; k += 1) arr[i + k] = incoming[k];
    // Pas de trou : une case vide avant `i` décalerait le code.
    const joined = arr.join('');
    const clean = commit(joined);
    focusAt(Math.min(clean.length, length - 1));
  };

  const handleChange = (i) => (e) => {
    const raw = e.target.value;
    // La case contenait déjà un chiffre : la nouvelle frappe est celle qui ne
    // correspond pas à l'ancien contenu.
    const typed = digits[i] && raw.length > 1 && raw.startsWith(digits[i]) ? raw.slice(1) : raw;
    if (!typed.replace(/\D/g, '')) {
      if (raw === '') {
        const arr = digits.slice();
        arr.splice(i, 1);
        commit(arr.join(''));
      }
      return;
    }
    writeFrom(i, typed);
  };

  const handleKeyDown = (i) => (e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = digits.slice();
      if (arr[i]) {
        arr.splice(i, 1);
        commit(arr.join(''));
        focusAt(i);
      } else if (i > 0) {
        arr.splice(i - 1, 1);
        commit(arr.join(''));
        focusAt(i - 1);
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusAt(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusAt(i + 1);
    }
  };

  const handlePaste = (i) => (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (!text) return;
    e.preventDefault();
    writeFrom(Math.min(i, value.length), text);
  };

  return (
    <div className="otp" role="group" aria-label={label}>
      <span className="form-label otp__label" id={`${idPrefix}-label`}>{label}</span>
      <div className="otp__boxes">
        {digits.map((d, i) => (
          <input
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            id={`${idPrefix}-${i}`}
            name={i === 0 ? `${idPrefix}-code` : undefined}
            className={`otp__box${d ? ' otp__box--filled' : ''}${error ? ' otp__box--error' : ''}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            aria-label={`Chiffre ${i + 1} sur ${length}`}
            aria-invalid={error || undefined}
            value={d}
            disabled={disabled}
            onChange={handleChange(i)}
            onKeyDown={handleKeyDown(i)}
            onPaste={handlePaste(i)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>
    </div>
  );
};

export default OtpInput;
